/**
 * Prompt Area Component
 * Input area with dropdowns and submit button
 */

import React from 'react';
import { OptionOnSelectData, SelectionEvents } from '@fluentui/react-components';
import ValidatorDropdown from 'components/ValidatorDropdown';
import LanguageDropdown from 'components/LanguageDropdown';
import CountryDropdown from 'components/LegislationDropdown';
import { ChatModeDropdown, ChatModeType } from 'components/ChatModeDropdown';

interface PromptAreaProps {
  // Mode
  validationMode: boolean;
  
  // Validation mode fields
  elementType: string;
  elementTypeError?: string;
  country: string;
  
  // Chat mode fields
  prompt: string;
  onPromptChange: (prompt: string) => void;
  legislationCountry: string;
  chatMode: ChatModeType;
  
  // Common fields
  validator: string;
  language: string;
  
  // Callbacks
  onValidatorSelected: (ev: SelectionEvents, data: OptionOnSelectData) => void;
  onLanguageSelected: (ev: SelectionEvents, data: OptionOnSelectData) => void;
  onLegislationCountrySelected: (ev: SelectionEvents, data: OptionOnSelectData) => void;
  onChatModeSelected: (ev: SelectionEvents, data: OptionOnSelectData) => void;
  
  // Submit
  onSubmit: () => void;
  isSubmitDisabled: boolean;
  submitTooltip?: string;
  isStreaming: boolean;
}

export function PromptArea({
  validationMode,
  elementType,
  elementTypeError,
  country,
  prompt,
  onPromptChange,
  legislationCountry,
  chatMode,
  validator,
  language,
  onValidatorSelected,
  onLanguageSelected,
  onLegislationCountrySelected,
  onChatModeSelected,
  onSubmit,
  isSubmitDisabled,
  submitTooltip,
  isStreaming
}: PromptAreaProps) {
  return (
    <ul className={"prompt-wrapper" + (!validationMode ? " large" : "")}>
      <li>
        <textarea 
          className={'promptInput ' + (validationMode ? "closed" : "opened")} 
          autoFocus 
          name="prompt"
          value={prompt} 
          onChange={e => onPromptChange(e.target.value)}
          placeholder={validationMode ? "" : chatMode === 'legislation' ? "Ask a question about building regulations..." : "Ask a question about your BIM model..."}
        />
        <div className='info-grid'>
          {validationMode && (
            <>
              <span className="selectedElement">Selected Element: &nbsp;</span>
              <span className="selectedElementValue" role={elementTypeError ? 'alert' : undefined}>
                <b>{elementTypeError || (elementType === '' ? 'N/A' : elementType)}</b>
              </span>
            </>
          )}
          {validationMode && (
            <>
              <div className="selectedCountry">Country: &nbsp;</div>
              <div className="selectedCountryValue"><b>{country}</b></div>
            </>
          )}
          {!validationMode && (
            <ChatModeDropdown 
              mode={chatMode}
              onModeSelected={onChatModeSelected} 
            />
          )}
          {!validationMode && (
            <ValidatorDropdown 
              validator={validator} 
              onValidatorSelected={onValidatorSelected} 
            />
          )}
          {!validationMode && chatMode === 'legislation' && (
            <CountryDropdown 
              country={legislationCountry}
              onCountrySelected={onLegislationCountrySelected} 
            />
          )}
          {!validationMode && chatMode === 'project' && (
            <>
              <span></span>
              <span></span>
            </>
          )}
          {!validationMode && (
            <LanguageDropdown 
              language={language} 
              onLanguageSelected={onLanguageSelected} 
            />
          )}
          {validationMode && (
            <ValidatorDropdown 
              validator={validator} 
              onValidatorSelected={onValidatorSelected} 
            />
          )}
          {validationMode && (
            <LanguageDropdown 
              language={language} 
              onLanguageSelected={onLanguageSelected} 
            />
          )}
        </div>
      </li>
      <li>
        <button 
          onClick={onSubmit} 
          disabled={isSubmitDisabled}
          title={submitTooltip}
        >
          <span className={`validate material-symbols-rounded${isStreaming ? ' stop' : ''}`}>
            {isStreaming ? 'stop_circle' : 'arrow_circle_right'}
          </span>
        </button>
      </li>
    </ul>
  );
}

export default PromptArea;
